import { renderAsync } from 'docx-preview';
import { FC, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { MappingEditorStore } from '../../../../store/mapping-editor.store';
import { highlightPreviewVariables } from '../../../../utils/highlight-preview-variables.util';

export type DocumentPreviewProps = {
  allFieldsAsMapped?: boolean;
  docxArrayBuffer?: ArrayBuffer;
  file?: File | undefined;
  fileName?: string;
  hideVariableHighlighting?: boolean;
};

export const DocumentPreview: FC<DocumentPreviewProps> = ({
  allFieldsAsMapped = false,
  docxArrayBuffer,
  file,
  fileName,
  hideVariableHighlighting = false,
}) => {
  const allFieldsAsMappedRef = useRef(allFieldsAsMapped);
  const hideVariableHighlightingRef = useRef(hideVariableHighlighting);
  const containerRef = useRef<HTMLDivElement>(null);
  const variableMappingsRef = useRef<Record<string, string>>({});
  const sectionsRef = useRef<HTMLElement[]>([]);

  allFieldsAsMappedRef.current = allFieldsAsMapped;
  hideVariableHighlightingRef.current = hideVariableHighlighting;

  const variableMappings = MappingEditorStore(({ variableMappings }) => variableMappings);

  useEffect(() => {
    variableMappingsRef.current = variableMappings;
  }, [variableMappings]);

  useEffect(() => {
    if (!containerRef.current) return;
    if (!docxArrayBuffer && !file) {
      containerRef.current.innerHTML = '';
      sectionsRef.current = [];
      return;
    }

    const container = containerRef.current;
    container.innerHTML = '';
    sectionsRef.current = [];

    const loadPreview = async (): Promise<ArrayBuffer | null> => {
      if (docxArrayBuffer) return docxArrayBuffer;
      if (file) return file.arrayBuffer();
      return null;
    };

    void loadPreview().then((buffer) => {
      if (!buffer || !containerRef.current) return;
      return renderAsync(buffer, container, undefined, {
        className: 'docx-preview',
        renderHeaders: true,
        renderFooters: true,
        renderFootnotes: true,
        ignoreWidth: false,
        ignoreHeight: false,
        breakPages: true,
      }).then(() => {
        const sections = Array.from(
          container.querySelectorAll<HTMLElement>('section.docx-preview'),
        );
        sectionsRef.current = sections;
        highlightPreviewVariables(container, {
          allFieldsAsMapped: allFieldsAsMappedRef.current,
          hideVariableHighlighting: hideVariableHighlightingRef.current,
          variableMappings: variableMappingsRef.current,
        });
      });
    });
  }, [docxArrayBuffer, file]);

  useEffect(() => {
    if (containerRef.current) {
      highlightPreviewVariables(containerRef.current, {
        allFieldsAsMapped,
        hideVariableHighlighting,
        variableMappings,
      });
    }
  }, [variableMappings, allFieldsAsMapped, hideVariableHighlighting]);

  return (
    <main
      className="relative min-w-0 flex-1 overflow-auto rounded-xl bg-white px-3 py-4"
      style={{
        boxShadow:
          '0 -1px 3px 0 rgba(16, 24, 40, 0.04), 0 2px 3px 0 rgba(16, 24, 40, 0.10), 0 1px 2px 0 rgba(16, 24, 40, 0.06)',
      }}
    >
      {fileName ? (
        <p className="mb-3 w-full min-w-0 truncate pb-2 text-xs text-gray-500" title={fileName}>
          {fileName}
        </p>
      ) : null}
      <div className={twMerge('relative mx-auto w-fit')}>
        <div ref={containerRef} />
      </div>
    </main>
  );
};
